import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ChevronDown, Download, FileText, LayoutGrid, Settings, ShieldCheck, Smartphone, X } from "lucide-react";
import { bootstrapPublicSession, signedPost } from "../lib/signedRequest";
import { webDownloadTargets } from "../lib/appDownload";
import { GeneralReportDialog } from "./GeneralReportDialog";

export const TERMS_VERSION = "2026-08-01";
const storageKey = (identityId) => `ol_public_chat_terms_${TERMS_VERSION}:${identityId || "anonymous"}`;

export function hasAcceptedPublicChatTerms(identityId) {
  try { return window.localStorage.getItem(storageKey(identityId)) === "1"; }
  catch { return false; }
}

function rememberAcceptance(identityId) {
  try { window.localStorage.setItem(storageKey(identityId), "1"); } catch {}
}

export function PublicChatGate({ identityId, topicTitle, onAccepted, onOpenDocument }) {
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const accept = async () => {
    if (!confirmed || busy) return;
    setBusy(true); setError("");
    const payload = {
      identityId,
      termsVersion: TERMS_VERSION,
      acceptedAt: new Date().toISOString(),
    };
    try {
      try {
        await signedPost("/api/v1/legal/acceptance", payload);
      } catch (firstError) {
        // On a fresh Android registration the HttpOnly session cookie can
        // become visible a moment after the profile state. Bootstrap once and
        // retry here so the user never has to close and reopen the app.
        await bootstrapPublicSession();
        await new Promise((resolve) => window.setTimeout(resolve, 150));
        await signedPost("/api/v1/legal/acceptance", payload);
      }
      rememberAcceptance(identityId);
      onAccepted();
    } catch {
      setError("Non è stato possibile registrare l’accettazione. Controlla la connessione e riprova.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm p-2 sm:p-4 flex items-stretch sm:items-center justify-center overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="public-chat-title">
      <section className="relative w-full max-w-xl h-full sm:h-auto max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)] card border-t-4 border-t-primary overflow-hidden flex flex-col pb-[76px] sm:pb-0">
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-7 pb-2 sm:pb-4">
          <div className="flex gap-3 items-start">
          <span className="hidden sm:flex w-11 h-11 rounded-full bg-primary/10 text-primary items-center justify-center shrink-0"><AlertTriangle size={23}/></span>
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-primary font-bold">Prima di entrare in {topicTitle || "questo argomento"}</p>
            <h2 id="public-chat-title" className="text-xl sm:text-3xl mt-1 leading-tight">Spazio vocale pubblico e confronto diretto</h2>
          </div>
        </div>
        <div className="mt-3 sm:mt-5 space-y-2 sm:space-y-3 text-[13px] sm:text-sm leading-5 sm:leading-6 text-ink">
          <p>I messaggi sono pubblici e il confronto può essere duro, con critiche, parolacce o espressioni offensive. Ciò che pubblichi potrà essere ascoltato e contestato.</p>
          <p><strong>L’accettazione non autorizza contenuti illegali.</strong> Restano vietati minacce, persecuzioni, diffusione illecita di dati personali, violenza, sfruttamento sessuale, terrorismo e ogni altro illecito.</p>
          <p>I messaggi pubblici possono essere conservati fino a un anno, salvo cancellazione anticipata o obblighi di tutela e di legge.</p>
        </div>
        <div className="mt-3 sm:mt-4 flex flex-wrap gap-x-3 sm:gap-x-4 gap-y-1.5 sm:gap-y-2 text-xs sm:text-sm">
          <button className="text-primary underline font-semibold" onClick={() => onOpenDocument("terms")}>Condizioni d’uso</button>
          <button className="text-primary underline font-semibold" onClick={() => onOpenDocument("privacy")}>Privacy</button>
          <button className="text-primary underline font-semibold" onClick={() => onOpenDocument("rules")}>Regole della comunità</button>
        </div>
        <label className="mt-3 sm:mt-5 flex gap-2 sm:gap-3 items-start rounded-xl border border-edge bg-cream p-2 sm:p-3 cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={(e)=>setConfirmed(e.target.checked)} className="mt-1 accent-primary"/>
          <span className="text-xs sm:text-sm leading-5">Ho almeno 18 anni, ho letto l’avviso e accetto le Condizioni d’uso e le Regole della comunità. Comprendo che il confronto è pubblico e può essere offensivo.</span>
        </label>
          {error && <p className="text-sm text-primary mt-3" role="alert">{error}</p>}
        </div>
        <div className="absolute sm:static inset-x-0 bottom-0 z-10 shrink-0 border-t border-edge bg-white/95 backdrop-blur px-3 sm:px-7 py-2 sm:py-3 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <button className="big-cta !py-2.5 sm:!py-3.5 text-sm" disabled={!confirmed || busy} onClick={accept} data-testid="accept-public-chat-terms">
            {busy ? "Registrazione in corso…" : "Accetta ed entra nella chat pubblica"}
          </button>
        </div>
      </section>
    </div>
  );
}

export const legalDocuments = {
  privacy: { title: "Informativa privacy", body: [
    ["Titolare e contatti", "Il servizio You Voice tratta i dati necessari al funzionamento della piattaforma. Per richieste privacy: info@opinione-libera.it."],
    ["Dati trattati", "Identificativo pseudonimo, chiavi pubbliche, nome scelto, avatar, messaggi e allegati pubblicati, reazioni, segnalazioni, dati tecnici di sicurezza e registrazione delle condizioni accettate. Le 12 parole di recupero e le chiavi private restano sul dispositivo."],
    ["Finalità e basi giuridiche", "Erogazione del servizio e sicurezza: esecuzione delle condizioni d’uso; prevenzione abusi e tutela del servizio: legittimo interesse e obblighi di legge; trattamenti facoltativi o non necessari: consenso separato quando richiesto."],
    ["Visibilità", "Messaggi, avatar e pseudonimo pubblicati nelle aree pubbliche sono accessibili agli altri utenti e possono essere condivisi. I messaggi privati sono trattati secondo le funzioni di cifratura descritte nel servizio."],
    ["Conservazione", "I messaggi pubblici sono programmati per la cancellazione dopo un anno. Tempi diversi possono applicarsi a segnalazioni, contestazioni, obblighi di legge, sicurezza, backup tecnici e messaggi privati con timer."],
    ["Diritti", "Puoi chiedere accesso, rettifica, cancellazione, limitazione, portabilità e opposizione nei casi previsti dal GDPR, nonché proporre reclamo al Garante per la protezione dei dati personali."],
    ["Destinatari e trasferimenti", "Fornitori tecnici autorizzati possono trattare dati per hosting, sicurezza e moderazione. Eventuali trasferimenti fuori dallo SEE richiedono una base e garanzie conformi al GDPR."],
  ]},
  terms: { title: "Condizioni d’uso", body: [
    ["Servizio per adulti", "L’accesso è riservato a persone di almeno 18 anni."],
    ["Natura del confronto", "Il servizio tutela un confronto libero e anche aspro. L’utente prende atto che potrà incontrare linguaggio volgare, critiche e contenuti soggettivamente offensivi."],
    ["Nessuna rinuncia ai diritti", "L’accettazione non costituisce rinuncia alla tutela prevista dalla legge e non esonera gli autori dalle responsabilità per i propri contenuti."],
    ["Responsabilità dell’utente", "Ogni utente risponde di ciò che pubblica e non deve diffondere contenuti illegali, dati personali altrui senza base giuridica, minacce o materiale lesivo dei diritti di terzi."],
    ["Moderazione", "Il servizio può limitare, sospendere o rimuovere contenuti e account secondo regole trasparenti, con motivazione e strumenti di ricorso quando applicabili."],
  ]},
  rules: { title: "Regole della comunità", body: [
    ["Consentito", "Opinioni impopolari, satira, critica anche severa, parolacce e dissenso, purché non oltrepassino i limiti di legge e non diventino persecuzione mirata."],
    ["Vietato", "Minacce credibili, istigazione alla violenza, molestie reiterate, pubblicazione illecita di dati personali, sfruttamento sessuale, terrorismo, truffe e altri contenuti illegali."],
    ["Segnalazioni", "Usa Segnala per indicare contenuti potenzialmente illegali o contrari alle regole. Le decisioni automatizzate rilevanti devono poter essere riesaminate."],
  ]},
};

export function LegalDocuments({ initial = "privacy", onClose }) {
  const [active,setActive]=useState(initial);
  const doc=useMemo(()=>legalDocuments[active] || legalDocuments.privacy,[active]);
  return (
    <div className="fixed inset-0 z-[1100] bg-ink/60 p-0 sm:p-6 flex items-stretch sm:items-center justify-center overflow-hidden" role="dialog" aria-modal="true">
      <section className="card w-full max-w-3xl h-[100dvh] sm:h-auto sm:max-h-[calc(100dvh-3rem)] overflow-hidden flex flex-col rounded-none sm:rounded-2xl">
        <header className="p-4 border-b border-edge flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck className="text-primary"/><h2 className="text-2xl">{doc.title}</h2></div>
          <button onClick={onClose} aria-label="Chiudi" className="p-2"><X/></button>
        </header>
        <nav className="px-4 py-2 border-b border-edge flex flex-wrap gap-2">
          {Object.entries(legalDocuments).map(([key,value])=><button key={key} onClick={()=>setActive(key)} className={`px-3 py-1.5 rounded-full text-sm ${active===key?"bg-ink text-white":"bg-cream"}`}>{value.title}</button>)}
        </nav>
        <div className="p-5 overflow-y-auto space-y-5 text-sm leading-6">
          {doc.body.map(([title,body])=><section key={title}><h3 className="text-lg mb-1">{title}</h3><p>{body}</p></section>)}
          <p className="text-xs text-muted border-t border-edge pt-4">Versione {TERMS_VERSION}.</p>
        </div>
      </section>
    </div>
  );
}


export function PublicLegalPage({ initial = "privacy" }) {
  const doc = legalDocuments[initial] || legalDocuments.privacy;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${doc.title} | You Voice`;

    let description = document.querySelector('meta[name="description"]');
    const createdDescription = !description;
    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }
    const previousDescription = description.getAttribute("content");
    description.setAttribute("content", `${doc.title} del servizio You Voice.`);

    return () => {
      document.title = previousTitle;
      if (createdDescription) description.remove();
      else if (previousDescription === null) description.removeAttribute("content");
      else description.setAttribute("content", previousDescription);
    };
  }, [doc.title]);

  return (
    <main className="min-h-screen bg-cream px-4 py-8 sm:px-6 sm:py-12 text-ink">
      <article className="mx-auto w-full max-w-3xl rounded-2xl border border-edge bg-white shadow-sm overflow-hidden">
        <header className="p-5 sm:p-8 border-b border-edge">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">You Voice</p>
          <div className="mt-2 flex items-center gap-3">
            <ShieldCheck className="text-primary shrink-0" />
            <h1 className="text-3xl sm:text-4xl">{doc.title}</h1>
          </div>
          <nav className="mt-5 flex flex-wrap gap-2" aria-label="Documenti legali">
            <a href="/privacy" className={`px-3 py-1.5 rounded-full text-sm ${initial === "privacy" ? "bg-ink text-white" : "bg-cream"}`}>Informativa privacy</a>
            <a href="/terms" className={`px-3 py-1.5 rounded-full text-sm ${initial === "terms" ? "bg-ink text-white" : "bg-cream"}`}>Condizioni d’uso</a>
            <a href="/community-rules" className={`px-3 py-1.5 rounded-full text-sm ${initial === "rules" ? "bg-ink text-white" : "bg-cream"}`}>Regole della comunità</a>
          </nav>
        </header>
        <div className="p-5 sm:p-8 space-y-6 text-base leading-7">
          {doc.body.map(([title, body]) => (
            <section key={title}>
              <h2 className="text-xl mb-1">{title}</h2>
              <p>{body}</p>
            </section>
          ))}
          <p className="text-sm text-muted border-t border-edge pt-5">Versione {TERMS_VERSION}.</p>
        </div>
      </article>
    </main>
  );
}

export function LegalMenu({ onOpen, onOpenAdmin, onOpenTopics }) {
  const [open,setOpen]=useState(false);
  const [reportOpen,setReportOpen]=useState(false);
  const downloads = useMemo(() => webDownloadTargets(), []);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", close);
    };
  }, [open]);

  const panel = open ? createPortal(
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" role="presentation">
      <button type="button" className="absolute inset-0 bg-ink/55 backdrop-blur-sm" onClick={()=>setOpen(false)} aria-label="Chiudi menu" />
      <section className="relative z-[1051] w-[min(92vw,380px)] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl border-2 border-ink bg-cream p-4 shadow-2xl" role="dialog" aria-modal="true" aria-label="Menu You Voice">
        <header className="sticky top-0 z-10 -mx-1 -mt-1 mb-3 flex items-center justify-between rounded-2xl bg-cream px-2 py-1">
          <div><p className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">You Voice</p><h2 className="text-xl font-bold">Menu</h2></div>
          <button type="button" onClick={()=>setOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full border border-edge bg-white" aria-label="Chiudi"><X size={20}/></button>
        </header>
        {onOpenTopics && (
          <button
            type="button"
            className="mb-2 w-full flex items-center gap-2 px-3 py-3 rounded-xl border-b border-edge hover:bg-white text-sm font-semibold"
            onClick={()=>{setOpen(false);onOpenTopics();}}
            data-testid="menu-topics"
          >
            <LayoutGrid size={16}/> Argomenti
          </button>
        )}
        {downloads.length > 0 && <div className="mb-2 pb-2 border-b border-edge">
          <p className="px-3 py-1 text-[11px] uppercase tracking-wider text-muted font-bold flex items-center gap-1"><Smartphone size={13}/> App mobile</p>
          {downloads.map((item) => item.url ? (
            <a key={item.key} href={item.url} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-2 px-3 py-3 rounded-xl hover:bg-white text-sm" onClick={()=>setOpen(false)} data-testid={`download-${item.key}`}>
              <Download size={15}/>{item.label}
            </a>
          ) : (
            <div key={item.key} className="px-3 py-3 text-sm text-muted flex items-center justify-between gap-2" data-testid={`download-${item.key}-pending`}>
              <span>{item.label}</span><span className="text-[10px] font-semibold">Prossimamente</span>
            </div>
          ))}
        </div>}
        <button type="button" className="mb-2 w-full flex items-center gap-2 border-b border-edge px-3 py-3 rounded-xl hover:bg-white text-sm font-semibold" onClick={()=>{setOpen(false);setReportOpen(true);}} data-testid="menu-general-report"><AlertTriangle size={16}/> Segnala un problema</button>
        {[[ "privacy","Privacy" ],[ "terms","Condizioni d’uso" ],[ "rules","Regole della comunità" ]].map(([key,label])=>(
          <button key={key} className="w-full text-left px-3 py-3 rounded-xl hover:bg-white text-sm font-semibold" onClick={()=>{setOpen(false);onOpen(key);}}>{label}</button>
        ))}
        {onOpenAdmin && <button className="w-full text-left px-3 py-3 mt-1 border-t border-edge rounded-xl hover:bg-white text-sm flex items-center gap-2 font-semibold" onClick={()=>{setOpen(false);onOpenAdmin();}} data-testid="open-admin"><Settings size={15}/> Area amministratore</button>}
      </section>
    </div>,
    document.body,
  ) : null;

  return <div className="relative z-50 shrink-0 pointer-events-auto" data-testid="legal-menu">
    <button type="button" className="w-9 h-9 flex shrink-0 touch-manipulation items-center justify-center text-muted hover:text-ink" onClick={()=>setOpen(true)} aria-label="Apri menu" aria-haspopup="dialog" aria-expanded={open}><FileText size={19}/><ChevronDown size={12}/></button>
    {panel}
    {reportOpen && <GeneralReportDialog onClose={()=>setReportOpen(false)} />}
  </div>;
}
